import { createApi } from "@reduxjs/toolkit/query/react";
import type { AppDispatch } from "../../app/store";
import { ApiEnum } from "../enums/ApiEnum";
import { apiBaseQuery } from "../utils/apiBaseQuery";
import { TranslationsInterface } from "../interfaces/shared/TranslationsInterface";
import { UserInterface } from "../interfaces/user/UserInterface";
import {
    mapDealerUserResponse,
    V2DealerUserResponse,
} from "./adapters/mapDealerUserResponse";
import { mapTranslationsResponse } from "./adapters/mapTranslationsResponse";

const refetchAllEndpointsOnLocalisationChange = async (
    _args: unknown,
    {
        dispatch,
        queryFulfilled,
    }: {
        dispatch: AppDispatch;
        queryFulfilled: Promise<unknown>;
    },
): Promise<void> => {
    try {
        await queryFulfilled;
        const { resetAllApiStates } = await import("./resetAllApiStates");
        resetAllApiStates(dispatch);
    } catch {
        // PATCH failed — localisation didn't change, nothing to refetch.
    }
};

export const userApiSlice = createApi({
    reducerPath: "rootApi",
    baseQuery: apiBaseQuery(),
    tagTypes: ["User"],
    endpoints: (builder) => ({
        getUserInfo: builder.query<UserInterface, void>({
            query: () => "apiV2/dealer/me",
            transformResponse: (response: V2DealerUserResponse) =>
                mapDealerUserResponse(response),
            providesTags: ["User"],
        }),
        setUserLanguage: builder.mutation<
            void,
            { languageId: number; countryId: number }
        >({
            query: (params) => ({
                url: "apiV2/dealer/me/localisation",
                method: "PATCH",
                body: {
                    active_language_id: params.languageId,
                    active_country_id: params.countryId,
                },
            }),
            onQueryStarted: refetchAllEndpointsOnLocalisationChange,
        }),
        setUserCountry: builder.mutation<void, number>({
            query: (countryId) => ({
                url: "apiV2/dealer/me/localisation",
                method: "PATCH",
                body: { active_country_id: countryId },
            }),
            onQueryStarted: refetchAllEndpointsOnLocalisationChange,
        }),
        getTranslations: builder.query<TranslationsInterface, void>({
            query: () => "apiV2/dealer/translations",
            transformResponse: mapTranslationsResponse,
        }),
        logout: builder.mutation<void, void>({
            query: () => ({
                url: ApiEnum.LOGOUT,
                method: "POST",
                credentials: "include",
            }),
        }),
        markRefurbishedVisited: builder.mutation<void, void>({
            query: () => ({
                url: "apiV2/dealer/me/refurbished-visited",
                method: "POST",
            }),
            invalidatesTags: ["User"],
        }),
    }),
});

export const {
    useGetUserInfoQuery,
    useSetUserLanguageMutation,
    useSetUserCountryMutation,
    useGetTranslationsQuery,
    useLazyGetUserInfoQuery,
    useLogoutMutation,
    useMarkRefurbishedVisitedMutation,
} = userApiSlice;
